import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";
import { addCustomFonts } from "../../../utils/commonComponent/common";

const UnitechHeader = "../img/quotation_header.png";
const UnitechFooter = "../img/cover_letter_footer.png";

let config = {
  margin: 35,
  headerHeight: 115,    // space reserved for header
  headerImgHeight: 90,  // image height for header
  footerHeight: 60,     // space reserved for footer
  footerImgHeight: 50,  // image height for footer
  fontSize: 10,
  lineHeight: 15,
  signImgWidth: 160,    // signature image rendered width in pt  ← increase to make bigger
  signImgMaxHeight: 80, // signature image max height cap in pt  ← increase to allow taller
};

// Loads an image URL and returns a base64 data URL string
const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL("image/png"), width: img.width, height: img.height });
    };
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
};

export async function generalCorrespondentsPDf(inputStates, setDownloadPdfloading) {
  setDownloadPdfloading(true);
  try {
    const doc = new jsPDF({
      orientation: "p",
      unit: "pt",
      format: "a4",
      compress: true,
    });

    await addCustomFonts(doc);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usableWidth = pageWidth - config.margin * 2;

    const addHeaderFooter = () => {
      // Header Image
      if (UnitechHeader) {
        doc.addImage(
          UnitechHeader,
          "PNG",
          config.margin,
          10,
          usableWidth,
          config.headerImgHeight
        );
      }

      // Footer Image
      if (UnitechFooter) {
        doc.addImage(
          UnitechFooter,
          "PNG",
          config.margin,
          pageHeight - config.footerHeight,
          usableWidth,
          config.footerImgHeight
        );
      }
    };

    const contentYStart = config.headerHeight + 10;
    let y = contentYStart;

    // ── Page break guard 
    // new page and resets y — preventing content from overlapping the footer.
    const checkPageBreak = () => {
      if (y + config.lineHeight > pageHeight - config.footerHeight) {
        doc.addPage();
        addHeaderFooter();
        y = contentYStart;
      }
    };

    addHeaderFooter(); // first page

    doc.setFont("helvetica", "normal");
    doc.setFontSize(config.fontSize);

    // ===== Letter Number & Date =====
    doc.setFont("helvetica", "bold");
    checkPageBreak();
    doc.text(`REF: ${inputStates.letterNumber || ""}`, config.margin, y);
    doc.text(
      `Date: ${inputStates.date ? moment(inputStates.date).format("DD/MM/YYYY") : ""}`,
      pageWidth - config.margin - 85,
      y
    );
    y += config.lineHeight * 2;

    // ===== To & Client Name =====
    // clientName may contain \n-separated address lines — render each line
    doc.setFont("helvetica", "bold");
    checkPageBreak();
    doc.text("To,", config.margin, y);
    y += config.lineHeight;

    if (inputStates.clientName) {
      // Split on \n first (explicit line breaks from data), then wrap long lines
      const clientRawLines = inputStates.clientName.split("\n");
      clientRawLines.forEach((rawLine) => {
        const wrappedLines = doc.splitTextToSize(rawLine.trim(), usableWidth);
        wrappedLines.forEach((line) => {
          checkPageBreak();
          doc.text(line, config.margin, y);
          y += config.lineHeight;
        });
      });
    }
    y += config.lineHeight; // one blank line after address block

    // ===== Subject =====
    if (inputStates.subject) {
      checkPageBreak();
      doc.setFont("helvetica", "bold");
      doc.text("Subject:", config.margin, y);

      // doc.setFont("helvetica", "normal");
      const subjectLines = doc.splitTextToSize(inputStates.subject, usableWidth - 60);
      doc.text(subjectLines, config.margin + 50, y);
      // Advance by the number of wrapped lines the subject occupies — no extra gap added
      y += subjectLines.length * config.lineHeight + config.lineHeight;
    }

    // ===== Content Paragraphs =====
    // content may contain \n for new lines and \n\n for paragraph breaks.
    // Leading whitespace/indentation is trimmed to avoid misalignment.
    if (inputStates.content) {
      doc.setFont("helvetica", "normal");
      const paragraphs = inputStates.content.split("\n");

      paragraphs.forEach((para) => {
        // Blank / whitespace-only line → single blank line gap
        if (!para.trim()) {
          y += config.lineHeight;
          return;
        }

        // Trim leading spaces/tabs (the data has indentation before ref lines)
        const cleanedPara = para.trim();
        const lines = doc.splitTextToSize(cleanedPara, usableWidth);
        lines.forEach((line) => {
          checkPageBreak();
          doc.text(line, config.margin, y);
          y += config.lineHeight;
        });
        // No extra gap after each line-group — blank \n lines in source control spacing
      });

      y += config.lineHeight; // one blank line after entire content block
    }

    // ===== Signature Block =====
    let signImgData = null;
    let signImgWidth = config.signImgWidth;
    let signImgHeight = config.signImgMaxHeight; // default fallback height

    if (inputStates.signPath && inputStates.signPath.trim()) {
      try {
        const result = await loadImageAsBase64(inputStates.signPath.trim());
        // Scale proportionally based on signImgWidth, but cap at signImgMaxHeight
        const aspectRatio = result.height / result.width;
        const proportionalHeight = signImgWidth * aspectRatio;
        signImgHeight = Math.min(proportionalHeight, config.signImgMaxHeight);
        // If height was capped, scale width down to maintain aspect ratio
        if (proportionalHeight > config.signImgMaxHeight) {
          signImgWidth = config.signImgMaxHeight / aspectRatio;
        }
        signImgData = result.dataUrl;
      } catch (e) {
        console.warn("Could not load signature image:", e.message);
        signImgData = null;
      }
    }

    // Pre-calculate name/designation lines
    const nameDesigLines = [];
    if (inputStates.nameDesignation && inputStates.nameDesignation.trim()) {
      inputStates.nameDesignation.split("\n").forEach((line) => {
        if (line.trim()) nameDesigLines.push(line.trim());
      });
    }
    const nameDesigHeight = nameDesigLines.length * config.lineHeight;

    // Pre-calculate attachment lines
    const attachmentLines = [];
    if (inputStates.attachments && inputStates.attachments.trim()) {
      doc.setFont("helvetica", "normal");
      doc.splitTextToSize(inputStates.attachments.trim(), usableWidth).forEach((l) => {
        attachmentLines.push(l);
      });
    }
    const attachmentHeight =
      attachmentLines.length > 0
        ? config.lineHeight + attachmentLines.length * config.lineHeight
        : 0;

    const signAreaHeight = signImgData
      ? signImgHeight + config.lineHeight
      : config.lineHeight * 4;

    const companyLineHeight = config.lineHeight;

    const totalSignatureHeight =
      signAreaHeight + companyLineHeight + nameDesigHeight + attachmentHeight;

    // Push whole signature block to new page if it doesn't fit
    const availableSpace = pageHeight - config.footerHeight - y;
    if (availableSpace < totalSignatureHeight) {
      doc.addPage();
      addHeaderFooter();
      y = contentYStart;
    }

    // ── Render signature image or blank signing space ──
    if (signImgData) {
      doc.addImage(signImgData, "PNG", config.margin, y, signImgWidth, signImgHeight);
      y += signImgHeight + config.lineHeight;
    } else {
      y += config.lineHeight * 4;
    }

    doc.setFont("helvetica", "bold");
    doc.text("For DYC Global Inspections Pvt. Ltd.", config.margin, y);
    y += config.lineHeight;

    // Name & Designation
    if (nameDesigLines.length > 0) {
      nameDesigLines.forEach((line) => {
        doc.setFont("helvetica", "bold");
        doc.text(line, config.margin, y);
        y += config.lineHeight;
      });
    }

    // Attachments
    if (attachmentLines.length > 0) {
      y += config.lineHeight; // one blank line before attachments
      doc.setFont("helvetica", "normal");
      attachmentLines.forEach((line) => {
        doc.text(line, config.margin, y);
        y += config.lineHeight;
      });
    }

    doc.save(`GeneralCorrespondents_${inputStates.letterNumber || "report"}.pdf`);
    setDownloadPdfloading(false);
  } catch (error) {
    setDownloadPdfloading(false);
    console.error("PDF generation error:", error);
  }
}